'use client';

import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { createTheme } from '@mui/material/styles';
import { theme as baseTheme } from '@startengine-engineering/theme';
import { usePathname } from 'next/navigation';
import React, { useEffect, lazy } from 'react';

const LazyReactQueryDevtools =
  process.env.NODE_ENV === 'development'
    ? lazy(() =>
        // @ts-ignore - devDependency, not available in production builds
        import('@tanstack/react-query-devtools').then((mod: any) => ({
          default: mod.ReactQueryDevtools,
        })),
      )
    : (_props: any) => null;
import GlobalStyles from './GlobalStyles';
import PHProvider from './PHProvider';
import ReactQueryProvider from './ReactQueryProvider';
import ReduxProvider from './ReduxProvider';
import ThirdPartScripts from './ThirdPartyScripts';
import GlobalErrorBoundary from 'components/ErrorBoundary/GlobalErrorBoundary';
import LoginModalProvider from 'providers/LoginModalProvider';
import { safeParseLocalStorage } from 'utils/localStorageHelpers';
import { safePostHogCapture } from 'utils/posthogHelpers';

const headingFontStack = "'owners-text', 'IBM Plex Sans', sans-serif";

const theme = createTheme(baseTheme, {
  typography: {
    h1: { ...(baseTheme.typography?.h1 || {}), fontFamily: headingFontStack },
    h2: { ...(baseTheme.typography?.h2 || {}), fontFamily: headingFontStack },
    h3: { ...(baseTheme.typography?.h3 || {}), fontFamily: headingFontStack },
    h4: { ...(baseTheme.typography?.h4 || {}), fontFamily: headingFontStack },
    h5: { ...(baseTheme.typography?.h5 || {}), fontFamily: headingFontStack },
    h6: { ...(baseTheme.typography?.h6 || {}), fontFamily: headingFontStack },
  },
});

declare global {
  interface Window {
    gtag: any;
    webkit?: {
      messageHandlers?: any;
    };
  }
}

type ProviderWrapperProps = { children: React.ReactNode };

const OFFERING_ROUTE_PREFIX = '/offering/';

const ProviderWrapper = ({ children }: ProviderWrapperProps) => {
  const pathname = usePathname();
  const isHomepageV3QaSurface = pathname === '/' || pathname === '/private';
  const shouldShowReactQueryDevtools =
    process.env.NODE_ENV === 'development' && !isHomepageV3QaSurface;

  useEffect(() => {
    const isCampaignPageRoute = pathname?.startsWith(OFFERING_ROUTE_PREFIX) ?? false;
    if (isCampaignPageRoute) return;
    safePostHogCapture('$pageview', {});
  }, [pathname]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && localStorage?.getItem('consentMode')) {
        const consentMode = safeParseLocalStorage('consentMode');
        if (consentMode) {
          window.gtag('consent', 'update', consentMode);
        }
      }
    } catch {
      // localStorage access may be blocked due to security/privacy settings
      // Silently handle localStorage access errors
    }
  }, []);

  return (
    <div id="next-app">
      <GlobalErrorBoundary>
        <ThirdPartScripts />
        <GlobalStyles />
        <ThemeProvider theme={theme}>
          <PHProvider>
            <ReactQueryProvider>
              <ReduxProvider>
                {/* PD-34298: single shared LoginModal lives here so all
                    useLoginModal() callers across the app open the same
                    instance. Must be inside both ReactQueryProvider and
                    ReduxProvider — useLoginModal pulls dispatch + queryClient. */}
                <LoginModalProvider>{children}</LoginModalProvider>
              </ReduxProvider>
              {shouldShowReactQueryDevtools && (
                <React.Suspense fallback={null}>
                  <LazyReactQueryDevtools initialIsOpen={false} />
                </React.Suspense>
              )}
            </ReactQueryProvider>
          </PHProvider>
        </ThemeProvider>
      </GlobalErrorBoundary>
    </div>
  );
};

export default ProviderWrapper;
